import React, {
  MutableRefObject, useContext, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { push } from 'connected-react-router';

import RouteGetter from '../../../routes/RouteGetter';
import { LoadingFrame } from '../..';
import PrompterComponent from './PrompterComponent';
import {
  getScenesListRequest,
  getScenesRequest,
} from '../../../store/modules/scenes/actions';
import { selectListScenes } from '../../../store/modules/scenes/selectors';
import {
  selectCountdownCamera,
  selectStudioMode,
} from '../../../store/modules/studio/selectors';
import {
  setAutoScrollAudioService, setCountdownCamera, setRecordingAfterDelay,
  setSupportUserAudio,
} from '../../../store/modules/studio/actions';
import { SseContext } from '../../../services/SSE';
import { selectProject } from '../../../store/modules/projects/selectors';
import AudioService from '../../../services/studio/AudioService';
import { StudioMode } from '../../../configs/studio';

type Props = {
  refPrompter: MutableRefObject<HTMLDivElement | null>;
};

const Prompter = ({ refPrompter }: Props) => {
  const dispatch = useDispatch();
  const scenesList = useSelector(selectListScenes);
  const { addSource } = useContext(SseContext);
  const { id: projectId } = useParams<{id: string}>();
  const project = useSelector(selectProject);
  const isCountdown = useSelector(selectCountdownCamera);
  const studioMode = useSelector(selectStudioMode);

  useEffect(() => {
    /**
     * Prompter is slow IOS, selection.removeAllRanges fixed this bug
     * */
    const selection = window.getSelection();
    selection?.removeAllRanges();
    if (isCountdown) {
      dispatch(setCountdownCamera(false));
      dispatch(setRecordingAfterDelay(false));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (studioMode === StudioMode.TELEPROMPTER) {
      window.studioAudioService?.close();
    }
  }, [studioMode]);

  useEffect(() => {
    const resolve = () => { dispatch(setSupportUserAudio(true)); };
    const reject = () => {
      dispatch(setSupportUserAudio(false));
      dispatch(setAutoScrollAudioService(false));
    };
    AudioService.checkAudioSupport(resolve, reject);
  }, [dispatch]);

  useEffect(() => {
    if (!project.loading && project.error) {
      dispatch(push(RouteGetter.get('project').page(projectId)));
    }
  }, [project, projectId, dispatch]);

  useMemo(() => {
    scenesList.data?.items?.forEach((item) => {
      addSource({
        url: item.ref.sse_topic,
        onMessage: (data) => {
          if (data.action === 'updated') {
            dispatch(getScenesRequest(item.id));
          }
        },
      });
    });
  }, [dispatch, scenesList, addSource]);

  useEffect(() => {
    const currentProjectId = parseInt(projectId, 10);

    if (currentProjectId) {
      const query = {
        filter: {
          project_id: currentProjectId,
        },
      };

      if (!scenesList.data && !scenesList.loading) {
        dispatch(getScenesListRequest(query));
      } else if (scenesList.data && !scenesList.loading) {
        const projectFilter = scenesList.data.filters.active.find((item) => item.key === 'project_id');
        if (projectFilter) {
          const projectFilterId = parseInt(projectFilter.value.toString(), 10);
          if (projectFilterId && projectFilterId !== currentProjectId) {
            dispatch(getScenesListRequest(query));
          }
        }
      }
    }
  }, [dispatch, projectId, scenesList]);

  return (scenesList.loading
    ? <LoadingFrame />
    : (
      <PrompterComponent
        refPrompter={refPrompter}
      />
    ));
};

export default Prompter;
